import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button, Modal, Input, Select, Badge } from '../../components/ui';
import api from '../../services/api';
import axios from 'axios';

// Separate axios instance pointing directly at the LMS backend
// (admin api instance has baseURL = main backend, so we can't reuse it)
const lms = axios.create({
  baseURL: process.env.REACT_APP_LMS_URL || 'http://localhost:5004/api/v1',
  timeout: 15000,
});
lms.interceptors.request.use(cfg => {
  const token = localStorage.getItem('mh_token') || localStorage.getItem('spa_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const lmsAPI = {
  getCourse:      id     => lms.get(`/courses/${id}`),
  getByProgram:   pid    => lms.get(`/courses/program/${pid}`),
  createCourse:   d      => lms.post('/courses', d),
  updateCourse:   (id,d) => lms.put(`/courses/${id}`, d),
  createModule:   d      => lms.post('/modules', d),
  updateModule:   (id,d) => lms.put(`/modules/${id}`, d),
  deleteModule:   id     => lms.delete(`/modules/${id}`),
  createLesson:   d      => lms.post('/lessons', d),
  updateLesson:   (id,d) => lms.put(`/lessons/${id}`, d),
  deleteLesson:   id     => lms.delete(`/lessons/${id}`),
  createQuestion: d      => lms.post('/questions', d),
  updateQuestion: (id,d) => lms.put(`/questions/${id}`, d),
  deleteQuestion: id     => lms.delete(`/questions/${id}`),
  getCertQueue:   ()     => lms.get('/admin/cert-queue'),
  getCertStats:   ()     => lms.get('/admin/cert-stats'),
  approveCert:    (id,d) => lms.post(`/admin/cert-queue/${id}/approve`, d),
  rejectCert:     (id,d) => lms.post(`/admin/cert-queue/${id}/reject`, d),
  getSubmissions: p      => lms.get('/admin/submissions', { params: p }),
  gradeSubmission:(id,d) => lms.put(`/admin/submissions/${id}/grade`, d),
};

const LESSON_TYPES = [
  { value:'text',               label:'📄 Text Lesson',        color:'var(--blue-light)' },
  { value:'video',              label:'▶ Video',               color:'var(--purple-light)' },
  { value:'file',               label:'📎 File Download',      color:'var(--text-muted)' },
  { value:'live_session',       label:'🎥 Live Session',       color:'var(--green-light)' },
  { value:'quiz',               label:'🧠 Quiz',               color:'var(--accent)' },
  { value:'assignment',         label:'📝 Assignment',         color:'var(--yellow)' },
  { value:'practical_evidence', label:'📸 Practical Evidence', color:'var(--coral,#f06060)' },
  { value:'portfolio',          label:'🗂 Portfolio Project',  color:'var(--teal,#3ecf8e)' },
];

const typeColor = t => LESSON_TYPES.find(x => x.value === t)?.color || 'var(--text-muted)';
const typeLabel = t => LESSON_TYPES.find(x => x.value === t)?.label || t;

// ── Lesson Form fields by type ────────────────────────────
function LessonTypeFields({ form, setForm }) {
  const f = form;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const ta = { width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'9px 12px', fontSize:13, resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' };

  if (f.lesson_type === 'text') return (
    <div style={{ gridColumn:'1/-1' }}>
      <label style={lbl}>Content (HTML supported)</label>
      <textarea value={f.content||''} onChange={e=>set('content',e.target.value)} rows={8} style={ta}/>
    </div>
  );

  if (f.lesson_type === 'video') return (<>
    <div style={{ gridColumn:'1/-1' }}>
      <Input label="Video URL (YouTube embed or uploaded)" value={f.video_url||''} onChange={e=>set('video_url',e.target.value)} placeholder="https://youtube.com/embed/... or https://media.millehills.rw/..."/>
    </div>
    <Input label="Duration (seconds)" type="number" value={f.video_duration||''} onChange={e=>set('video_duration',e.target.value)}/>
  </>);

  if (f.lesson_type === 'file') return (<>
    <div style={{ gridColumn:'1/-1' }}>
      <Input label="File URL" value={f.file_url||''} onChange={e=>set('file_url',e.target.value)} placeholder="https://media.millehills.rw/uploads/..."/>
    </div>
    <Input label="File Name (shown to learner)" value={f.file_name||''} onChange={e=>set('file_name',e.target.value)} placeholder="Week1-Slides.pdf"/>
  </>);

  if (f.lesson_type === 'live_session') return (<>
    <div style={{ gridColumn:'1/-1' }}>
      <Input label="Meeting Link (Zoom / Google Meet)" value={f.live_url||''} onChange={e=>set('live_url',e.target.value)} placeholder="https://zoom.us/j/..."/>
    </div>
    <Input label="Date & Time" type="datetime-local" value={f.live_datetime||''} onChange={e=>set('live_datetime',e.target.value)}/>
    <Input label="Duration (minutes)" type="number" value={f.live_duration||''} onChange={e=>set('live_duration',e.target.value)} placeholder="90"/>
  </>);

  if (['quiz','assignment','practical_evidence','portfolio'].includes(f.lesson_type)) return (<>
    <div style={{ gridColumn:'1/-1' }}>
      <label style={lbl}>Instructions</label>
      <textarea value={f.instructions||''} onChange={e=>set('instructions',e.target.value)} rows={4} style={ta}
        placeholder={f.lesson_type === 'quiz' ? 'Instructions shown before the quiz starts...' :
                     f.lesson_type === 'practical_evidence' ? 'What practical work should the learner photograph or video?' :
                     f.lesson_type === 'portfolio' ? 'Describe the final project deliverable...' :
                     'What should the learner submit?'}/>
    </div>
    {f.lesson_type !== 'quiz' && <>
      <Input label="Max Score" type="number" value={f.max_score||100} onChange={e=>set('max_score',e.target.value)}/>
      <Input label="Pass Score" type="number" value={f.pass_score||70}  onChange={e=>set('pass_score',e.target.value)}/>
      <Input label="Due Date (optional)" type="datetime-local" value={f.due_date||''} onChange={e=>set('due_date',e.target.value)}/>
      <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer', alignSelf:'flex-end', paddingBottom:8 }}>
        <input type="checkbox" checked={f.allow_late!==false} onChange={e=>set('allow_late',e.target.checked)}/>
        <span style={{ color:'var(--text-secondary)' }}>Allow late submissions</span>
      </label>
    </>}
    {f.lesson_type === 'quiz' && <>
      <Input label="Max Attempts" type="number" value={f.max_attempts||3} onChange={e=>set('max_attempts',e.target.value)}/>
      <Input label="Pass Score (%)" type="number" value={f.pass_score||70} onChange={e=>set('pass_score',e.target.value)}/>
    </>}
  </>);

  return null;
}

// ── Quiz Question Editor ──────────────────────────────────
function QuizEditor({ lessonId, onClose }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [qForm, setQForm]         = useState(null);
  const [saving, setSaving]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`${LMS_URL}/lessons/${lessonId}`);
      setQuestions(r.data.data?.questions || []);
    } catch { setQuestions([]); } finally { setLoading(false); }
  }, [lessonId]);

  useEffect(() => { load(); }, [load]);

  const emptyQ = () => ({
    lesson_id: lessonId, question_type:'multiple_choice', question_text:'',
    options:[{ id:'a', text:'' },{ id:'b', text:'' },{ id:'c', text:'' },{ id:'d', text:'' }],
    correct_answers:['a'], explanation:'', points:1,
  });

  const saveQ = async () => {
    setSaving(true);
    try {
      if (qForm.id) await lmsAPI.updateQuestion(qForm.id, qForm);
      else          await lmsAPI.createQuestion(qForm);
      setQForm(null); load();
    } catch (e) { alert(e.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const deleteQ = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    await lmsAPI.deleteQuestion(id); load();
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontSize:13, color:'var(--text-muted)' }}>{questions.length} questions</span>
        <Button onClick={() => setQForm(emptyQ())} size="sm">+ Add Question</Button>
      </div>

      {loading ? <div style={{ color:'var(--text-muted)', fontSize:13 }}>Loading…</div> :
       questions.map((q, i) => (
        <div key={q.id} style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:8, padding:'12px 14px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
            <div style={{ fontSize:13, fontWeight:500, flex:1, paddingRight:12 }}>
              <span style={{ color:'var(--text-muted)', fontSize:11, marginRight:6 }}>Q{i+1}</span>
              {q.question_text}
            </div>
            <div style={{ display:'flex', gap:6, flexShrink:0 }}>
              <Button size="sm" variant="ghost" onClick={()=>setQForm(q)}>Edit</Button>
              <Button size="sm" variant="danger" onClick={()=>deleteQ(q.id)}>✕</Button>
            </div>
          </div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            {(q.options||[]).map(o => (
              <span key={o.id} style={{ fontSize:11, padding:'2px 8px', borderRadius:20,
                background: q.correct_answers?.includes(o.id) ? 'rgba(62,207,142,0.15)' : 'var(--bg-surface)',
                border: `1px solid ${q.correct_answers?.includes(o.id) ? '#3ecf8e' : 'var(--border)'}`,
                color: q.correct_answers?.includes(o.id) ? '#3ecf8e' : 'var(--text-muted)',
              }}>{o.id.toUpperCase()}. {o.text}</span>
            ))}
          </div>
        </div>
      ))}

      {qForm && (
        <Modal open onClose={() => setQForm(null)} title={qForm.id ? 'Edit Question' : 'New Question'} width={600}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div>
              <label style={lbl}>Question</label>
              <textarea value={qForm.question_text} onChange={e=>setQForm(p=>({...p,question_text:e.target.value}))} rows={3}
                style={{ width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'9px 12px', fontSize:13, resize:'vertical', boxSizing:'border-box' }}/>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {(qForm.options||[]).map((o,i) => (
                <div key={o.id} style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <input type="checkbox"
                    checked={qForm.correct_answers?.includes(o.id)}
                    onChange={e => setQForm(p => ({ ...p, correct_answers: e.target.checked
                      ? [...(p.correct_answers||[]), o.id]
                      : (p.correct_answers||[]).filter(x=>x!==o.id) }))}
                    title="Mark as correct"/>
                  <span style={{ fontSize:11, fontWeight:700, color:'var(--accent)', width:14 }}>{o.id.toUpperCase()}</span>
                  <input value={o.text} onChange={e => setQForm(p => ({ ...p, options: p.options.map((x,j) => j===i?{...x,text:e.target.value}:x) }))}
                    placeholder={`Option ${o.id.toUpperCase()}`}
                    style={{ flex:1, background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'7px 10px', fontSize:12 }}/>
                </div>
              ))}
            </div>
            <div>
              <Input label="Explanation (shown after answering)" value={qForm.explanation||''} onChange={e=>setQForm(p=>({...p,explanation:e.target.value}))} placeholder="Why is this the correct answer?"/>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <Input label="Points" type="number" value={qForm.points||1} onChange={e=>setQForm(p=>({...p,points:parseInt(e.target.value)||1}))} style={{ width:80 }}/>
              <div style={{ fontSize:11, color:'var(--text-muted)', alignSelf:'flex-end', paddingBottom:8 }}>
                ✓ Check boxes on the left to mark correct answers
              </div>
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <Button variant="secondary" onClick={()=>setQForm(null)}>Cancel</Button>
              <Button onClick={saveQ} loading={saving}>Save Question</Button>
            </div>
          </div>
        </Modal>
      )}

      <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:8, borderTop:'1px solid var(--border)' }}>
        <Button onClick={onClose}>Done</Button>
      </div>
    </div>
  );
}

// ── Main Course Builder ───────────────────────────────────
export default function CourseBuilderPage() {
  const { programId } = useParams();
  const navigate      = useNavigate();

  const [course,  setCourse]  = useState(null);
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const [activeModule, setActiveModule] = useState(null); // expanded module id
  const [modal, setModal]   = useState(null); // 'module' | 'lesson' | 'quiz' | 'cert'
  const [selMod, setSelMod] = useState(null);
  const [selLes, setSelLes] = useState(null);
  const [quizLessonId, setQuizLessonId] = useState(null);

  // Cert queue
  const [certQueue, setCertQueue] = useState([]);
  const [certStats, setCertStats] = useState(null);
  const [certTab,   setCertTab]   = useState('queue'); // queue | submissions
  const [submissions, setSubs]    = useState([]);
  const [gradeTarget, setGradeTarget] = useState(null);
  const [gradeForm,   setGradeForm]   = useState({});

  const [modForm, setModForm] = useState({ title:'', description:'', is_required:true });
  const [lesForm, setLesForm] = useState({ title:'', lesson_type:'text', is_required:true, estimated_minutes:15 });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      // Load program info
      const pRes = await api.get(`/programs/${programId}`).catch(() => null);
      if (pRes) setProgram(pRes.data?.data);

      // Try to load existing course for this program
      const cRes = await lmsAPI.getByProgram(programId).catch(() => null);
      if (cRes) { setCourse(cRes.data?.data); }
      else { setCourse(null); }
    } catch { setError('Failed to load'); } finally { setLoading(false); }
  }, [programId]);

  useEffect(() => { load(); }, [load]);

  const loadCertQueue = useCallback(async () => {
    const [q, s, sub] = await Promise.all([
      lmsAPI.getCertQueue().catch(() => ({ data:{ data:[] } })),
      lmsAPI.getCertStats().catch(() => ({ data:{ data:{} } })),
      lmsAPI.getSubmissions({ status:'submitted', limit:20 }).catch(() => ({ data:{ data:[] } })),
    ]);
    setCertQueue(q.data.data || []);
    setCertStats(s.data.data || {});
    setSubs(sub.data.data || []);
  }, []);

  useEffect(() => { if (modal === 'cert') loadCertQueue(); }, [modal, loadCertQueue]);

  // ── Course actions ────────────────────────────────────
  const createCourse = async () => {
    if (!program) return;
    setSaving(true);
    try {
      const r = await lmsAPI.createCourse({
        program_id:     programId,
        title:          program.title || program.name,
        description:    program.description || program.summary || '',
        pass_threshold: 70,
      });
      setCourse(r.data.data);
    } catch (e) { setError(e.response?.data?.error || 'Failed'); } finally { setSaving(false); }
  };

  const togglePublish = async () => {
    setSaving(true);
    try {
      const r = await lmsAPI.updateCourse(course.id, { is_published: !course.is_published });
      setCourse(r.data.data);
    } catch { } finally { setSaving(false); }
  };

  // ── Module actions ────────────────────────────────────
  const saveModule = async () => {
    setSaving(true);
    try {
      if (selMod?.id) {
        await lmsAPI.updateModule(selMod.id, modForm);
      } else {
        await lmsAPI.createModule({ ...modForm, course_id: course.id });
      }
      setModal(null); load();
    } catch (e) { alert(e.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const deleteModule = async (id) => {
    if (!window.confirm('Delete this module and all its lessons?')) return;
    await lmsAPI.deleteModule(id); load();
  };

  // ── Lesson actions ────────────────────────────────────
  const saveLesson = async () => {
    if (!lesForm.title?.trim()) { alert('Lesson title required'); return; }
    setSaving(true);
    try {
      // Sanitize: convert empty strings to null, strings to proper types
      const int  = (v) => v===''||v===null||v===undefined ? null : parseInt(v,10)||null;
      const str  = (v) => v===''||v===null||v===undefined ? null : v;
      const bool = (v) => v !== false;

      const payload = {
        title:             lesForm.title?.trim(),
        lesson_type:       lesForm.lesson_type,
        is_required:       bool(lesForm.is_required),
        estimated_minutes: int(lesForm.estimated_minutes) || 15,
        course_id:         course.id,
        // Type-specific fields — null if empty
        content:           str(lesForm.content),
        video_url:         str(lesForm.video_url),
        video_duration:    int(lesForm.video_duration),
        file_url:          str(lesForm.file_url),
        file_name:         str(lesForm.file_name),
        live_url:          str(lesForm.live_url),
        live_datetime:     str(lesForm.live_datetime) || null,
        live_duration:     int(lesForm.live_duration),
        instructions:      str(lesForm.instructions),
        max_score:         int(lesForm.max_score) || 100,
        pass_score:        int(lesForm.pass_score) || 50,
        max_attempts:      int(lesForm.max_attempts) || 3,
        due_date:          str(lesForm.due_date) || null,
        allow_late:        bool(lesForm.allow_late),
      };

      if (selLes?.id) await lmsAPI.updateLesson(selLes.id, payload);
      else            await lmsAPI.createLesson({ ...payload, module_id: selMod.id });
      setModal(null); load();
    } catch (e) { alert(e.response?.data?.error || e.message || 'Save failed'); } finally { setSaving(false); }
  };

  const deleteLesson = async (id) => {
    if (!window.confirm('Delete this lesson?')) return;
    await lmsAPI.deleteLesson(id); load();
  };

  // ── Cert queue actions ────────────────────────────────
  const approveCert = async (id) => {
    await lmsAPI.approveCert(id, { notes:'Approved by trainer' });
    loadCertQueue();
  };

  const rejectCert = async (id, notes) => {
    await lmsAPI.rejectCert(id, { notes });
    loadCertQueue();
  };

  const gradeSubmission = async () => {
    setSaving(true);
    try {
      await lmsAPI.gradeSubmission(gradeTarget.id, gradeForm);
      setGradeTarget(null); loadCertQueue();
    } catch (e) { alert(e.response?.data?.error || 'Grade failed'); } finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ padding:60, textAlign:'center', color:'var(--text-muted)' }}>Loading course…</div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24 }}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:4 }}>
            <Link to="/programs" style={{ color:'var(--text-muted)', textDecoration:'none' }}>Programs</Link>
            {' → '} Course Builder
          </div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:700 }}>
            {program?.title || program?.name || 'Course Builder'}
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {course && (
            <>
              <Button variant="secondary" onClick={() => setModal('cert')}>
                🎓 Cert Queue {certStats?.eligible > 0 ? `(${certStats.eligible})` : ''}
              </Button>
              <Button onClick={togglePublish} loading={saving}
                style={{ background: course.is_published ? 'rgba(240,96,96,0.15)' : 'rgba(62,207,142,0.15)',
                  color: course.is_published ? '#f06060' : '#3ecf8e',
                  border: `1px solid ${course.is_published ? 'rgba(240,96,96,0.3)' : 'rgba(62,207,142,0.3)'}` }}>
                {course.is_published ? '⏸ Unpublish' : '▶ Publish Course'}
              </Button>
            </>
          )}
        </div>
      </div>

      {error && <div style={{ padding:'10px 14px', background:'rgba(240,96,96,0.1)', borderRadius:8, fontSize:13, color:'#f06060' }}>⚠ {error}</div>}

      {/* No course yet */}
      {!course && (
        <div style={{ background:'var(--bg-surface)', border:'2px dashed var(--border)', borderRadius:'var(--radius-lg)', padding:60, textAlign:'center' }}>
          <div style={{ fontSize:32, marginBottom:12, opacity:0.3 }}>◐</div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:600, marginBottom:8 }}>No course yet</div>
          <div style={{ fontSize:13, color:'var(--text-muted)', marginBottom:24 }}>
            Create a course for <strong>{program?.title || program?.name}</strong> to start adding lessons.
          </div>
          <Button onClick={createCourse} loading={saving}>Create Course</Button>
        </div>
      )}

      {/* Course exists */}
      {course && (
        <>
          {/* Course meta bar */}
          <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'14px 20px', display:'flex', gap:24, alignItems:'center', flexWrap:'wrap' }}>
            <div style={{ flex:1, minWidth:200 }}>
              <div style={{ fontSize:11, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>Course</div>
              <div style={{ fontWeight:600, fontSize:14 }}>{course.title}</div>
            </div>
            {[
              ['Modules',  course.modules?.length || 0],
              ['Lessons',  course.module_count !== undefined ? course.lesson_count : course.modules?.reduce((a,m)=>a+(m.lessons?.length||0),0) || 0],
              ['Pass threshold', `${course.pass_threshold}%`],
            ].map(([k,v]) => (
              <div key={k} style={{ textAlign:'center' }}>
                <div style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:700, color:'var(--accent)' }}>{v}</div>
                <div style={{ fontSize:11, color:'var(--text-muted)' }}>{k}</div>
              </div>
            ))}
            <div style={{ display:'flex', gap:8 }}>
              <span style={{ fontSize:11, padding:'3px 10px', borderRadius:20, fontWeight:600,
                background: course.is_published ? 'rgba(62,207,142,0.12)' : 'rgba(107,114,128,0.12)',
                color: course.is_published ? '#3ecf8e' : '#6b7280',
                border: `1px solid ${course.is_published ? 'rgba(62,207,142,0.3)' : 'var(--border)'}` }}>
                {course.is_published ? '● Published' : '○ Draft'}
              </span>
            </div>
          </div>

          {/* Modules */}
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {(course.modules || []).map((mod, mi) => (
              <div key={mod.id} style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>

                {/* Module header */}
                <div style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 18px', cursor:'pointer', borderBottom: activeModule===mod.id ? '1px solid var(--border)' : 'none' }}
                  onClick={() => setActiveModule(activeModule===mod.id ? null : mod.id)}>
                  <span style={{ fontSize:11, fontWeight:700, color:'var(--accent)', width:20, flexShrink:0 }}>M{mi+1}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:14 }}>{mod.title}</div>
                    {mod.description && <div style={{ fontSize:11, color:'var(--text-muted)' }}>{mod.description}</div>}
                  </div>
                  <span style={{ fontSize:11, color:'var(--text-muted)' }}>{mod.lessons?.length||0} lessons</span>
                  <div style={{ display:'flex', gap:6 }} onClick={e=>e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={()=>{ setSelMod(mod); setModForm({ title:mod.title, description:mod.description||'', is_required:mod.is_required }); setModal('module'); }}>Edit</Button>
                    <Button size="sm" variant="danger" onClick={()=>deleteModule(mod.id)}>✕</Button>
                  </div>
                  <span style={{ color:'var(--text-muted)', fontSize:12 }}>{activeModule===mod.id ? '▲' : '▼'}</span>
                </div>

                {/* Lessons */}
                {activeModule === mod.id && (
                  <div style={{ padding:'12px 18px', display:'flex', flexDirection:'column', gap:'8' }}>
                    {(mod.lessons||[]).map((les, li) => (
                      <div key={les.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:'var(--bg-elevated)', borderRadius:8, border:'1px solid var(--border)' }}>
                        <span style={{ fontSize:10, color:'var(--text-muted)', width:24, flexShrink:0 }}>L{li+1}</span>
                        <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20, background:`${typeColor(les.lesson_type)}18`, color:typeColor(les.lesson_type), border:`1px solid ${typeColor(les.lesson_type)}44`, flexShrink:0 }}>
                          {typeLabel(les.lesson_type)}
                        </span>
                        <span style={{ flex:1, fontSize:13, fontWeight:500 }}>{les.title}</span>
                        {les.estimated_minutes && <span style={{ fontSize:11, color:'var(--text-muted)' }}>{les.estimated_minutes}m</span>}
                        {!les.is_required && <span style={{ fontSize:10, color:'var(--text-muted)', fontStyle:'italic' }}>optional</span>}
                        <div style={{ display:'flex', gap:6 }}>
                          {les.lesson_type === 'quiz' && (
                            <Button size="sm" variant="secondary" onClick={()=>{ setQuizLessonId(les.id); setModal('quiz'); }}>Questions</Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={()=>{ setSelMod(mod); setSelLes(les); setLesForm({ title:les.title, lesson_type:les.lesson_type, is_required:les.is_required, estimated_minutes:les.estimated_minutes||15, content:les.content||'', video_url:les.video_url||'', video_duration:les.video_duration||'', file_url:les.file_url||'', file_name:les.file_name||'', live_url:les.live_url||'', live_datetime:les.live_datetime?.slice(0,16)||'', live_duration:les.live_duration||'', instructions:les.instructions||'', max_score:les.max_score||100, pass_score:les.pass_score||70, max_attempts:les.max_attempts||3, due_date:les.due_date?.slice(0,16)||'', allow_late:les.allow_late!==false }); setModal('lesson'); }}>Edit</Button>
                          <Button size="sm" variant="danger" onClick={()=>deleteLesson(les.id)}>✕</Button>
                        </div>
                      </div>
                    ))}

                    {/* Add lesson button */}
                    <button onClick={()=>{ setSelMod(mod); setSelLes(null); setLesForm({ title:'', lesson_type:'text', is_required:true, estimated_minutes:15 }); setModal('lesson'); }}
                      style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'10px', borderRadius:8, border:'1.5px dashed var(--border)', background:'transparent', color:'var(--text-muted)', cursor:'pointer', fontSize:13, transition:'all 0.15s' }}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--accent)';e.currentTarget.style.color='var(--accent)';}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.color='var(--text-muted)';}}>
                      + Add Lesson
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* Add module button */}
            <button onClick={()=>{ setSelMod(null); setModForm({ title:'', description:'', is_required:true }); setModal('module'); }}
              style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'14px', borderRadius:'var(--radius-lg)', border:'1.5px dashed var(--border)', background:'transparent', color:'var(--text-muted)', cursor:'pointer', fontSize:13, transition:'all 0.15s' }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--accent)';e.currentTarget.style.color='var(--accent)';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.color='var(--text-muted)';}}>
              + Add Module
            </button>
          </div>
        </>
      )}

      {/* ── MODULE MODAL ── */}
      <Modal open={modal==='module'} onClose={()=>setModal(null)} title={selMod?.id ? 'Edit Module' : 'New Module'} width={500}>
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <Input label="Module Title *" value={modForm.title} onChange={e=>setModForm(p=>({...p,title:e.target.value}))} placeholder="e.g. Week 1: HTML Basics"/>
          <div>
            <label style={lbl}>Description (optional)</label>
            <textarea value={modForm.description} onChange={e=>setModForm(p=>({...p,description:e.target.value}))} rows={2}
              style={{ width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'9px 12px', fontSize:13, resize:'vertical', boxSizing:'border-box' }}/>
          </div>
          <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
            <input type="checkbox" checked={modForm.is_required} onChange={e=>setModForm(p=>({...p,is_required:e.target.checked}))}/>
            <span style={{ color:'var(--text-secondary)' }}>Required module (must complete to progress)</span>
          </label>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <Button variant="secondary" onClick={()=>setModal(null)}>Cancel</Button>
            <Button onClick={saveModule} loading={saving}>{selMod?.id ? 'Save' : 'Create Module'}</Button>
          </div>
        </div>
      </Modal>

      {/* ── LESSON MODAL ── */}
      <Modal open={modal==='lesson'} onClose={()=>setModal(null)} title={selLes?.id ? 'Edit Lesson' : `New Lesson — ${selMod?.title||''}`} width={660}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>

          <div style={{ gridColumn:'1/-1' }}>
            <Input label="Lesson Title *" value={lesForm.title} onChange={e=>setLesForm(p=>({...p,title:e.target.value}))}/>
          </div>

          <div style={{ gridColumn:'1/-1' }}>
            <label style={lbl}>Lesson Type</label>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
              {LESSON_TYPES.map(t => (
                <button key={t.value} onClick={()=>setLesForm(p=>({...p,lesson_type:t.value}))}
                  style={{ padding:'8px 6px', borderRadius:8, border:`1.5px solid ${lesForm.lesson_type===t.value?t.color:'var(--border)'}`, background:lesForm.lesson_type===t.value?`${t.color}18`:'var(--bg-elevated)', color:lesForm.lesson_type===t.value?t.color:'var(--text-muted)', fontSize:11, cursor:'pointer', transition:'all 0.15s', textAlign:'center', fontWeight:lesForm.lesson_type===t.value?700:400 }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <LessonTypeFields form={lesForm} setForm={setLesForm}/>

          <Input label="Est. Duration (min)" type="number" value={lesForm.estimated_minutes||15} onChange={e=>setLesForm(p=>({...p,estimated_minutes:parseInt(e.target.value)||15}))} style={{ width:120 }}/>

          <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer', alignSelf:'flex-end', paddingBottom:8 }}>
            <input type="checkbox" checked={lesForm.is_required!==false} onChange={e=>setLesForm(p=>({...p,is_required:e.target.checked}))}/>
            <span style={{ color:'var(--text-secondary)' }}>Required lesson</span>
          </label>

          <div style={{ gridColumn:'1/-1', display:'flex', gap:10, justifyContent:'flex-end', paddingTop:6, borderTop:'1px solid var(--border)' }}>
            <Button variant="secondary" onClick={()=>setModal(null)}>Cancel</Button>
            <Button onClick={saveLesson} loading={saving}>{selLes?.id ? 'Save Changes' : 'Add Lesson'}</Button>
          </div>
        </div>
      </Modal>

      {/* ── QUIZ QUESTION EDITOR ── */}
      <Modal open={modal==='quiz'} onClose={()=>setModal(null)} title="Quiz Questions" width={680}>
        {quizLessonId && <QuizEditor lessonId={quizLessonId} onClose={()=>setModal(null)}/>}
      </Modal>

      {/* ── CERT QUEUE MODAL ── */}
      <Modal open={modal==='cert'} onClose={()=>setModal(null)} title="Certification & Grading" width={740}>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Stats */}
          {certStats && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10 }}>
              {[['Eligible',certStats.eligible,'#3ecf8e'],['Pending',certStats.pending_approval,'var(--accent)'],['Issued',certStats.issued,'var(--blue-light)'],['Submissions',submissions.length,'var(--purple-light)']].map(([k,v,c])=>(
                <div key={k} style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 14px', textAlign:'center' }}>
                  <div style={{ fontSize:20, fontWeight:700, color:c }}>{v||0}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)' }}>{k}</div>
                </div>
              ))}
            </div>
          )}

          {/* Sub-tabs */}
          <div style={{ display:'flex', gap:4, borderBottom:'1px solid var(--border)' }}>
            {[['queue','Cert Queue'],['submissions','Pending Submissions']].map(([k,l])=>(
              <button key={k} onClick={()=>setCertTab(k)} style={{ padding:'8px 16px', fontSize:13, cursor:'pointer', background:'none', border:'none', borderBottom:`2px solid ${certTab===k?'var(--accent)':'transparent'}`, color:certTab===k?'var(--accent)':'var(--text-muted)', fontWeight:certTab===k?600:400 }}>{l}</button>
            ))}
          </div>

          {/* Cert queue */}
          {certTab==='queue' && (
            <div style={{ display:'flex', flexDirection:'column', gap:10, maxHeight:380, overflowY:'auto' }}>
              {certQueue.length===0 ? <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>No learners eligible yet</div> :
               certQueue.map(el => (
                <div key={el.id} style={{ display:'flex', gap:12, alignItems:'center', padding:'12px 14px', background:'var(--bg-elevated)', borderRadius:8, border:'1px solid var(--border)' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:13 }}>{el.learner_name}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)' }}>{el.program_title} · {el.completion_pct}% · avg {el.avg_score}%</div>
                  </div>
                  <span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background:'rgba(62,207,142,0.12)', color:'#3ecf8e', border:'1px solid rgba(62,207,142,0.3)' }}>Eligible</span>
                  <div style={{ display:'flex', gap:6 }}>
                    <Button size="sm" onClick={()=>approveCert(el.id)} style={{ background:'rgba(62,207,142,0.15)', color:'#3ecf8e', border:'1px solid rgba(62,207,142,0.3)' }}>✓ Issue Cert</Button>
                    <Button size="sm" variant="ghost" onClick={()=>{ const n=window.prompt('Reason for rejection?'); if(n) rejectCert(el.id,n); }}>Reject</Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pending submissions */}
          {certTab==='submissions' && (
            <div style={{ display:'flex', flexDirection:'column', gap:10, maxHeight:380, overflowY:'auto' }}>
              {submissions.length===0 ? <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>No pending submissions</div> :
               submissions.map(s => (
                <div key={s.id} style={{ padding:'12px 14px', background:'var(--bg-elevated)', borderRadius:8, border:'1px solid var(--border)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                    <div>
                      <div style={{ fontWeight:600, fontSize:13 }}>{s.learner_name}</div>
                      <div style={{ fontSize:11, color:'var(--text-muted)' }}>{s.lesson_title} · {s.lesson_type} · {new Date(s.submitted_at).toLocaleDateString('en-RW')}</div>
                    </div>
                    <Button size="sm" onClick={()=>{ setGradeTarget(s); setGradeForm({ score:100, feedback:'', status:'approved' }); }}>Grade</Button>
                  </div>
                  {s.text_response && <div style={{ fontSize:12, color:'var(--text-secondary)', padding:'8px 10px', background:'var(--bg-surface)', borderRadius:6, marginBottom:6 }}>{s.text_response.slice(0,150)}{s.text_response.length>150?'…':''}</div>}
                  {s.file_urls?.length>0 && <div style={{ fontSize:11, color:'var(--blue-light)' }}>📎 {s.file_urls.length} file(s) attached</div>}
                  {s.link_url && <a href={s.link_url} target="_blank" rel="noopener noreferrer" style={{ fontSize:11, color:'var(--blue-light)' }}>🔗 {s.link_url}</a>}
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* ── GRADE MODAL ── */}
      {gradeTarget && (
        <Modal open onClose={()=>setGradeTarget(null)} title={`Grade — ${gradeTarget.lesson_title}`} width={480}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <Select label="Decision" value={gradeForm.status} onChange={e=>setGradeForm(p=>({...p,status:e.target.value}))}>
              <option value="approved">✓ Approved</option>
              <option value="resubmit_requested">↩ Request Resubmission</option>
              <option value="rejected">✕ Rejected</option>
            </Select>
            <Input label="Score (0-100)" type="number" value={gradeForm.score} onChange={e=>setGradeForm(p=>({...p,score:parseInt(e.target.value)}))}/>
            <div>
              <label style={lbl}>Feedback to learner</label>
              <textarea value={gradeForm.feedback} onChange={e=>setGradeForm(p=>({...p,feedback:e.target.value}))} rows={4}
                placeholder="Explain your decision, what was good, what needs improvement..."
                style={{ width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'9px 12px', fontSize:13, resize:'vertical', boxSizing:'border-box' }}/>
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <Button variant="secondary" onClick={()=>setGradeTarget(null)}>Cancel</Button>
              <Button onClick={gradeSubmission} loading={saving}>Submit Grade</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

const lbl = { fontSize:12, color:'var(--text-secondary)', fontWeight:500, textTransform:'uppercase', letterSpacing:'0.03em', display:'block', marginBottom:6 };